import { Auth } from '@lib/auth';
import oyenAPI from '@lib/oyenAPI';
import { UploadFile } from 'antd/lib/upload/interface';
import { createHash } from 'crypto';
import type { FieldPathValue, Path, UseFormSetValue } from 'react-hook-form';
import {
  AGE_BREED_EFFECTIVE_DATE,
  AGE_BREED_START_DATE,
  SPECIAL_CHARACTER_MAP,
} from './constant';
import { dayjs } from '@lib/dayjs';
import { Dayjs } from 'dayjs';
import { TFunction } from 'next-i18next';
import catBasic from '@assets/images/plan/cat-basic.svg';
import catChampion from '@assets/images/plan/cat-champion.svg';
import catPlus from '@assets/images/plan/cat-plus.svg';
import { ClaimType } from '@prisma/client';

// eslint-disable-next-line import/prefer-default-export
export const capitalizeFirstLetter = (text: string | undefined) =>
  text ? text[0].toUpperCase() + text.slice(1) : '';

export const convertAmount = (
  type: 'FromDB' | 'ToDB',
  amount?: number | null
) => {
  let convertedAmount = 0;
  if (amount) {
    if (type === 'FromDB') {
      convertedAmount = amount / 100;
    } else {
      convertedAmount = Math.trunc(amount * 100);
    }
  }
  return convertedAmount;
};

export function roundFloatToCents(value: number | string) {
  if (isNaN(Number(value))) return 0;

  return Math.round(Number(value) * 100);
}

export const isEmptyBankDetails = (object: any) =>
  !object || Object.values(object).every((x) => x === '');

export const parseStringBoolean = (boolean: string) =>
  boolean.toLowerCase().trim() === 'true';

export const isEmpty = (object: any) =>
  !object || Object.values(object).every((x) => x === '' || x === undefined);

export const getNum = (val: any) => +val || 0;

export const sortAlphabetically = (elements: any[]) =>
  elements.sort(
    (a, b) => a.localeCompare(b) //using String.prototype.localCompare()
  );

export const sendFile = async (
  auth: Auth | null,
  authToken: () => Promise<string> | string,
  file: File,
  category: string
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileName', file.name);
  formData.append('type', file.type);
  formData.append('category', category);

  if (auth?.isAuthenticated) {
    const { data } = await oyenAPI(authToken()).then((api) =>
      api.post('/api/uploader', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    );
    return data.signedUrl;
  }
};

export const handleMultipleFiles = async (
  auth: Auth | null,
  authToken: () => Promise<string> | '',
  fileList: File[],
  category: string
) =>
  Promise.all(
    fileList.map(async (file: File) =>
      sendFile(auth, authToken, file, category)
    )
  );

export const handleFileUpload = async <FieldType extends Record<string, any>>(
  file: File | UploadFile,
  type: 'beforeUpload' | 'onRemove',
  fileList: File[],
  setFileList: (v: File[]) => void,
  setValue?: UseFormSetValue<FieldType>,
  key?: Path<FieldType>
) => {
  // const imageUrl = 'https://file-examples-com.github.io/uploads/2017/10/file_example_JPG_100kB-sample.png';
  let update = [...fileList];
  // console.log('handleFileUpload', type, file, [...fileList]);
  if (type === 'onRemove') {
    const index = fileList.findIndex((f) => {
      // console.log('findindex111', f, file);
      f.name === file.name;
    });
    const newFileList = fileList.slice();
    newFileList.splice(index, 1);
    // console.log('newFileList', newFileList);
    setFileList(newFileList);
    update = newFileList;
  } else {
    setFileList(update);
  }
  // console.log('{key, setvalue}', { key, setValue });
  if (key && setValue) {
    // console.log('handlefileupload setvalue', key, update);

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setValue(key, update as FieldPathValue<FieldType, Path<FieldType>>, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    // setValue(key, ['aaa'], { shouldValidate: true });
  }
};

export const handleFileRemove = async (
  file: UploadFile,
  fileList: File[],
  setFileList: (v: File[]) => void
) => {
  const index = fileList.findIndex((f) => f.name === file.name);
  const newFileList = [...fileList.slice()];
  newFileList.splice(index, 1);
  setFileList(newFileList);
};

export const hash = (string: string) =>
  createHash('sha256').update(string).digest('hex');

export function replaceSpecialCharacters(input: string): string {
  let output = input;

  for (const [symbol, words] of Object.entries(SPECIAL_CHARACTER_MAP)) {
    for (const word of words) {
      const regex = new RegExp('\\b' + word + '\\b', 'gi');
      output = output.replace(regex, '');
    }
    // Remove the special character symbol as well
    output = output.replace(new RegExp('\\' + symbol, 'g'), '');
  }

  return output;
}

export const parseStringToNoSpecialNoSpace = (str: string) => {
  const parsedStr = replaceSpecialCharacters(str.trim().toLocaleLowerCase())
    .split(' ')
    .join('');

  return parsedStr;
};

export const parseParamsIntoObject = (query: any) => {
  const params = new URLSearchParams(query);
  const paramsObject: { [key: string]: string | string[] } = {};

  for (const [key, value] of params.entries()) {
    if (paramsObject[key]) {
      if (Array.isArray(paramsObject[key])) {
        (paramsObject[key] as string[]).push(value);
      } else {
        paramsObject[key] = [paramsObject[key] as string, value];
      }
    } else {
      paramsObject[key] = value;
    }
  }

  return paramsObject;
};

export const resolveLocale = (locale: string, isTakaful: boolean) => {
  switch (locale) {
    case 'en':
    case 'eni':
      return isTakaful ? 'eni' : 'en';
    case 'my':
    case 'myi':
      return isTakaful ? 'myi' : 'my';
    default:
      return isTakaful ? 'eni' : 'en';
  }
};

export const titleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(' ')
    .map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

export const mappedCopaymentKey = (copaymentRate: string) => {
  switch (copaymentRate) {
    case '50':
      return 'copayment_discount_35';
    case '70':
      return 'copayment_discount_15';
    case '90':
      return 'copayment_discount_0';
    default:
      return 'copayment_discount_15';
  }
};

export const isAgeBreedRenewal = (date: Date | string | Dayjs) => {
  return (
    dayjs() >= dayjs(AGE_BREED_EFFECTIVE_DATE) &&
    dayjs(date) >= dayjs(AGE_BREED_START_DATE)
  );
};
export const formatPrice = (price = 0, decimal = 2) => {
  return price.toLocaleString(undefined, {
    minimumFractionDigits: decimal,
    maximumFractionDigits: decimal,
  });
};

export const mapCopaymentSlugToRate = (copaymentSlug: string) => {
  switch (copaymentSlug) {
    case 'copayment_discount_0':
      return 10;
    case 'copayment_discount_5':
      return 20;
    case 'copayment_discount_15':
      return 30;
    case 'copayment_discount_35':
      return 50;
    default:
      return 10;
  }
};

export function allStorage() {
  const archive: any = {};
  const keys = Object.keys(localStorage);
  let i = keys.length;

  while (i--) {
    archive[keys[i]] = localStorage.getItem(keys[i]);
  }

  return archive;
}

export const reformatTakafulPlanName = (value: string | null, t: TFunction) => {
  return (
    t(reformatPlanName(value), {
      petType: 'Kucing',
    }) + ' Takaful'
  );
};

export const planImage = (value: string) => {
  if (!value) throw new Error('Invalid plan');

  const planName = value.toLowerCase();

  if (planName.includes('basic')) {
    return catBasic;
  }

  if (planName.includes('plus')) {
    return catPlus;
  }

  if (planName.includes('champion')) {
    return catChampion;
  }

  throw new Error('Invalid plan name provided');
};

export const reformatPlanName = (value: string | null) => {
  if (!value) return 'planPlus';

  const planName = value.toLowerCase();

  if (planName.includes('basic')) {
    return 'planBasic';
  }

  if (planName.includes('plus')) {
    return 'planPlus';
  }

  if (planName.includes('champion')) {
    return 'planChampion';
  }

  throw new Error('Invalid plan name passed');
};

export const stemmingPlanName = (value: string | null) => {
  if (!value) return 'planPlus';

  const planName = value.toLowerCase();

  if (planName.includes('basic')) {
    return 'basic';
  }

  if (planName.includes('plus')) {
    return 'plus';
  }

  if (planName.includes('champion')) {
    return 'champion';
  }

  throw new Error('Invalid plan name passed');
};

export const calculateWaitingPeriod = (
  policy: {
    isBuyback: boolean;
    parentPolicyId: number;
    isNewMSIGPolicyWording: boolean;
  },
  claimType: ClaimType
) => {
  if (!policy?.isBuyback && policy?.parentPolicyId) return 0;

  if (claimType == 'VET_MEDICAL_ACCIDENTAL_INJURY') {
    return 1;
  }

  if (policy?.isNewMSIGPolicyWording) return 30;

  return 14;
};

export const getPlanTypeFromPlanName = (planName: string) => {
  const matched = planName.match(/(basic)|(plus)|(champion)/gi);

  if (!matched || matched.length === 0) {
    return 'basic';
  }

  return matched[0].toLowerCase();
};
